import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * A section as SectionsNav needs it — a subset shared by the server `Section`
 * and the builder's `DraftSection` (both carry id/title and optionally the
 * homework flag once phase 2 lands it).
 */
export type NavSection = {
  id: string;
  title: string;
  is_homework?: boolean;
};

export type SectionsNavProps = {
  sections: NavSection[];
  /** id of the currently-active section (highlighted). */
  activeId?: string;
  onSelect: (id: string) => void;
  /**
   * 'read'  — player: a jump list only.
   * 'edit'  — builder: adds "add section" / "add homework" affordances.
   */
  mode?: 'read' | 'edit';
  onAddSection?: () => void;
  onAddHomework?: () => void;
  /** edit-only: drag-reorder the normal sections. `toIdx` is the index within
   * the numbered (non-homework) list. */
  onReorder?: (fromId: string, toIdx: number) => void;
};

/**
 * SectionsNav — the right-hand "Разделы" panel shared by the student player
 * (read) and the lesson builder (edit), mirroring Edvibe. The homework section
 * (is_homework) is pulled out and shown separately at the bottom with a home
 * icon, never mixed into the numbered section list.
 */
export function SectionsNav({
  sections,
  activeId,
  onSelect,
  mode = 'read',
  onAddSection,
  onAddHomework,
  onReorder,
}: SectionsNavProps) {
  const { t } = useTranslation();
  const normal = sections.filter((s) => !s.is_homework);
  const homework = sections.find((s) => s.is_homework);
  const draggable = mode === 'edit' && !!onReorder;
  const [dragId, setDragId] = useState<string | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  return (
    <nav className="rounded-card bg-surface-container-lowest p-4">
      <p className="mb-3 font-display text-lg font-bold tracking-display text-on-surface">
        {t('sectionsNav.title')}
      </p>

      <ul className="space-y-1">
        {normal.map((s, idx) => (
          <li
            key={s.id}
            draggable={draggable}
            onDragStart={draggable ? (e) => { setDragId(s.id); e.dataTransfer.effectAllowed = 'move'; } : undefined}
            onDragOver={draggable ? (e) => { if (dragId && dragId !== s.id) { e.preventDefault(); setOverIdx(idx); } } : undefined}
            onDragLeave={draggable ? () => setOverIdx((o) => (o === idx ? null : o)) : undefined}
            onDrop={draggable ? (e) => { e.preventDefault(); if (dragId) onReorder?.(dragId, idx); setDragId(null); setOverIdx(null); } : undefined}
            onDragEnd={draggable ? () => { setDragId(null); setOverIdx(null); } : undefined}
            className={cn(
              'rounded-button transition',
              overIdx === idx && dragId && dragId !== s.id && 'ring-2 ring-primary/60',
              dragId === s.id && 'opacity-50',
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(s.id)}
              className={cn(
                'flex w-full items-start gap-2 rounded-button px-3 py-2 text-left text-sm transition',
                s.id === activeId
                  ? 'bg-primary-container text-on-primary-container font-semibold'
                  : 'text-on-surface-variant hover:bg-surface-container',
              )}
            >
              {draggable ? (
                <span className="material-symbols-outlined mt-px shrink-0 cursor-grab text-base opacity-50" title={t('sectionsNav.dragToReorder')}>
                  drag_indicator
                </span>
              ) : (
                <span className="mt-px h-4 w-1 shrink-0 rounded-pill bg-current opacity-60" />
              )}
              <span className="flex-1 leading-snug">{s.title || t('sectionsNav.untitled')}</span>
            </button>
          </li>
        ))}
      </ul>

      {mode === 'edit' && (
        <button
          type="button"
          onClick={onAddSection}
          className="mt-2 flex w-full items-center gap-2 rounded-button px-3 py-2 text-sm text-primary hover:bg-surface-container"
        >
          <span className="material-symbols-outlined text-base">add</span>
          {t('sectionsNav.addSection')}
        </button>
      )}

      {(homework || mode === 'edit') && (
        <div className="mt-3 border-t border-outline-variant/40 pt-3">
          <button
            type="button"
            onClick={() => (homework ? onSelect(homework.id) : onAddHomework?.())}
            className={cn(
              'flex w-full items-center gap-2 rounded-button px-3 py-2 text-left text-sm transition',
              homework && homework.id === activeId
                ? 'bg-tertiary-container text-on-tertiary-container font-semibold'
                : 'text-on-surface-variant hover:bg-surface-container',
            )}
          >
            <span className="material-symbols-outlined text-base">home</span>
            <span className="flex-1">{t('sectionsNav.homework')}</span>
          </button>
        </div>
      )}
    </nav>
  );
}
